import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { HazardCategory } from '@safetyhub/shared';
import { Button, Card, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';

interface Organization {
  id: string; // 구독 id — 이 화면에서는 select의 key로만 쓴다
  companyId: string; // 회사 id — `/admin/companies/:companyId/hazard-categories`가 받는 축
  name: string;
  createdAt: string;
  _count: { members: number; sites: number };
}

export default function HazardCategories() {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [confirm, confirmDialog] = useConfirmDialog();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [newLabel, setNewLabel] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 조직(구독) 목록 조회
  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => adminApi.getSubscriptions({ limit: 200 }),
  });
  const organizations: Organization[] = orgsData?.data || [];

  // 선택된 조직의 카테고리 목록 조회
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ['admin', 'hazard-categories', selectedCompanyId],
    queryFn: () => adminApi.getHazardCategories(selectedCompanyId),
    enabled: !!selectedCompanyId,
  });
  const categories: HazardCategory[] = categoriesData?.data || [];

  // 카테고리 생성
  const createMutation = useMutation({
    mutationFn: ({ companyId, label }: { companyId: string; label: string }) =>
      adminApi.createHazardCategory(companyId, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'hazard-categories', selectedCompanyId] });
      setNewLabel('');
      setShowCreateForm(false);
      showToast('카테고리가 생성되었습니다.', 'success');
    },
    onError: (error) => {
      showToast(extractApiError(error).message, 'error');
    },
  });

  // 카테고리 삭제
  const deleteMutation = useMutation({
    mutationFn: ({ companyId, categoryId }: { companyId: string; categoryId: string }) =>
      adminApi.deleteHazardCategory(companyId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'hazard-categories', selectedCompanyId] });
      setDeletingId(null);
      showToast('카테고리가 삭제되었습니다.', 'success');
    },
    onError: (error) => {
      setDeletingId(null);
      showToast(extractApiError(error).message, 'error');
    },
  });

  const handleCreate = () => {
    if (!selectedCompanyId || !newLabel.trim()) return;
    createMutation.mutate({ companyId: selectedCompanyId, label: newLabel.trim() });
  };

  const handleDelete = async (category: HazardCategory) => {
    if (!selectedCompanyId || !category.id) return;
    if (
      !(await confirm({
        title: '카테고리 삭제',
        body: `"${category.label}" 카테고리를 삭제하시겠습니까?`,
        confirmLabel: '삭제',
        danger: true,
      }))
    )
      return;
    setDeletingId(category.id);
    deleteMutation.mutate({ companyId: selectedCompanyId, categoryId: category.id });
  };

  const systemCategories = categories.filter((c) => c.isSystem);
  const customCategories = categories.filter((c) => !c.isSystem);

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">위험 카테고리 관리</h2>
          <p className="mt-1 text-sm text-gray-500">
            조직별 커스텀 위험 카테고리를 관리합니다.
          </p>
        </div>
        {selectedCompanyId && (
          <Button variant="primary" onClick={() => setShowCreateForm((v) => !v)}>
            <span>+</span>
            <span>카테고리 추가</span>
          </Button>
        )}
      </div>

      {/* 조직 선택 */}
      <Card radius="lg" elevation="none" className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">조직 선택</label>
        {orgsLoading ? (
          <div className="text-sm text-gray-400">조직 목록 로딩 중...</div>
        ) : (
          <select
            value={selectedCompanyId}
            onChange={(e) => {
              setSelectedCompanyId(e.target.value);
              setShowCreateForm(false);
            }}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
          >
            <option value="">-- 조직을 선택하세요 --</option>
            {organizations.map((org) => (
              // value는 회사 id — 카테고리 endpoint가 companyId를 받는다.
              // (key는 구독 id로 둔다: 목록 내 유일하고, 한 회사에 구독이 둘일 때도 안전)
              <option key={org.id} value={org.companyId}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </Card>

      {/* 카테고리 추가 폼 */}
      {showCreateForm && selectedCompanyId && (
        <Card radius="lg" elevation="none" className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">새 카테고리 추가</h3>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="카테고리 이름 (최대 50자)"
              maxLength={50}
              className="flex-1 max-w-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              disabled={!newLabel.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? '추가 중...' : '추가'}
            </Button>
            <button
              onClick={() => { setShowCreateForm(false); setNewLabel(''); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
            >
              취소
            </button>
          </div>
        </Card>
      )}

      {/* 카테고리 목록 */}
      {!selectedCompanyId ? (
        <div className="bg-white rounded-card-lg border p-12 text-center text-gray-400">
          조직을 선택하면 위험 카테고리 목록이 표시됩니다.
        </div>
      ) : categoriesLoading ? (
        <div className="bg-white rounded-card-lg border p-12 text-center text-gray-400">
          로딩 중...
        </div>
      ) : (
        <div className="space-y-6">
          {/* 커스텀 카테고리 */}
          <Card padding="none" radius="lg" elevation="none">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium text-gray-900">
                커스텀 카테고리
                <span className="ml-2 text-sm text-gray-500">({customCategories.length}개)</span>
              </h3>
              <button
                onClick={() => refetchCategories()}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                새로고침
              </button>
            </div>
            {customCategories.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                커스텀 카테고리가 없습니다.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이름</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">코드</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">사용 횟수</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">생성일</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customCategories.map((cat) => (
                    <tr key={cat.code} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{cat.label}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{cat.code}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{cat.usageCount}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {cat.createdAt ? new Date(cat.createdAt).toLocaleDateString('ko-KR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(cat)}
                          disabled={deletingId === cat.id || deleteMutation.isPending}
                          className="px-3 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === cat.id ? '삭제 중...' : '삭제'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* 시스템 카테고리 (읽기 전용) */}
          <Card padding="none" radius="lg" elevation="none">
            <div className="px-4 py-3 border-b">
              <h3 className="font-medium text-gray-900">
                시스템 기본 카테고리
                <span className="ml-2 text-sm text-gray-500">({systemCategories.length}개)</span>
              </h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이름</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">코드</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">사용 횟수</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {systemCategories.map((cat) => (
                  <tr key={cat.code} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{cat.label}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{cat.code}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{cat.usageCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
